<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import type { ComputedRef } from 'vue'
import BsNormalCard from '@/legacy/components/NormalCard.vue'
import BsProductSharingLikeIcon from '@/legacy/components/ProductSharingLikeIcon.vue'
import { createGAEvent } from '@/assets/ts/customEvent'
import {
	ProductSharingImageSize,
	getTimeAgoValue,
	kFormatter,
	MemberTypes,
} from '@/legacy/ts/productSharing'
import {
	getformatFloatPrice,
	getCurrencySymbolOriginal,
} from '@/assets/ts/currency'
import { useBreakpoints, getVueAssetImagePath } from '@/lib/utils'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const props = defineProps({
	post: {
		type: Object,
		default: null,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	columnCount: {
		type: Number,
		default: 1,
	},
	text: {
		type: Object,
		default: null,
	},
	isSearch: {
		type: Boolean,
		default: false,
	},
	isExplore: {
		type: Boolean,
		default: false,
	},
	isIndexLogin: {
		type: Boolean,
		default: false,
	},
	localCurrencyInfo: {
		type: Array,
	},
	isCurrentModeDefault: {
		type: Boolean,
		default: false,
	},
	supportedCurrencies: {
		type: Object,
	},
	warehouseIdMap: {
		type: Object,
	},
	gridId: {
		type: String,
	},
})

const emit = defineEmits(['like', 'view-post'])
const postItem = ref<HTMLElement>()
const likeIconCompoent = ref<InstanceType<
	typeof BsProductSharingLikeIcon
> | null>(null)
const paddingSpace = ref(6.5)
const currencySymbolOriginal = ref(null)
const { isSmall: isMobile } = useBreakpoints()

const postItemInfo: ComputedRef<any> = computed(() => {
	if (props.post === null) return null
	return {
		...props.post,
		postTimeDisplay: getTimeAgoValue(
			props.post.publishDate,
			props.text.timeago,
		),
	}
})

const postItemImageSrc = computed(() => {
	return getMedia(postItemInfo.value?.media[0])
})

const postSymbols = computed(() => {
	if (!postItemInfo.value?.media.length) return null
	return postItemInfo.value?.media[0].type === 'ps_video'
		? 'video'
		: postItemInfo.value?.media.length > 1
		  ? 'images'
		  : null
})

const calculateImageHeight = computed(() => {
	const target = props.gridId ? `#${props.gridId}` : '.mansory-grid'
	const $targetDiv = document.querySelector(target)
	const eleWidth = $targetDiv?.clientWidth ? $targetDiv.clientWidth : 200
	const targetWidth = eleWidth / props.columnCount - paddingSpace.value * 2
	return Math.round(targetWidth / parseFloat(postItemInfo.value.mediaRatio))
})

const totalLikeCountDisplay = computed(() => {
	if (postItemInfo.value.likeCount) {
		return kFormatter(postItemInfo.value.likeCount, props.text)
	}
	return 0
})

// const eventParams = computed(() => {
//   const defaultObj = { category: postItemInfo.value.slug || 'none', label: postItemInfo.value.id }
//   if (props.isSearch) return { ...defaultObj, value: 'search' }
//   if (props.isExplore) return { ...defaultObj, value: 'explore' }
//   if (props.isIndexLogin) return { ...defaultObj, value: 'home' }
//   return { ...defaultObj, value: 'public_profile' }
// })

const postId = computed(() => {
	if (props.post === null) return null
	return props.post.id
})

const isShowPrice = computed(() => {
	return props.isExplore && !props.isCurrentModeDefault
})

const isLocalCurrency = computed(() => {
	if (
		props.localCurrencyInfo &&
		props.localCurrencyInfo.length &&
		!props.isCurrentModeDefault
	) {
		const temp: any = props.localCurrencyInfo.filter(
			(item: any) => item.key === props.post.warehouse,
		)
		if (temp.length) {
			return temp[0].isLocal
		}
		return false
	}
	return false
})

const currencySymbol = computed(() => {
	if (!props.supportedCurrencies || !props.post.warehouse) return ''
	if (!props.warehouseIdMap?.[props.post.warehouse]) return null
	if (props.warehouseIdMap[props.post.warehouse].currency === undefined)
		return null
	const { currency } = props.warehouseIdMap[props.post.warehouse]
	return props.supportedCurrencies[currency.toLowerCase()].symbol
})

const localPricing = computed(() => {
	if (isLocalCurrency.value) {
		return getformatFloatPrice({
			price: props.post.ecLocalPrice,
		})
	}
	return ''
})

const priceOriginal = computed(() => {
	if (isShowPrice.value && props.post) {
		return `${currencySymbolOriginal.value} ${Number(
			props.post.ecPrice,
		).toLocaleString('en')}`
	}
	return ''
})

const isMemberOfficial1 = computed(() => {
	if (props.post?.user?.member_types?.length) {
		return props.post.user.member_types.includes(MemberTypes.Official1)
	}
	return false
})

const isMemberAssigned1 = computed(() => {
	if (props.post?.user?.member_types?.length) {
		return props.post.user.member_types.includes(MemberTypes.Assigned1)
	}
	return false
})

const isMemberAssigned2 = computed(() => {
	if (props.post?.user?.member_types?.length) {
		return props.post.user.member_types.includes(MemberTypes.Assigned2)
	}
	return false
})

function viewPost() {
	emit('view-post')
	createGAEvent('ps_active_user')
}

function updatePostInfo(postInfo: any) {
	createGAEvent('ps_active_user')
	// need to fixed, can not modify props directly
	props.post.likeCount = postInfo.likeCount
	props.post.isLiked = postInfo.isLiked
	emit('like', {
		id: postId.value,
		param: 'isLiked',
		val: postInfo.isLiked,
		likeCount: postInfo.likeCount,
		slug: props.post.slug,
	})
}

function getMedia(mediaObj: any) {
	if (!mediaObj || !Object.keys(mediaObj).length || !mediaObj.content)
		return null
	if (mediaObj.type === 'ps_video') return mediaObj.preview
	if (!mediaObj.has_resize) return mediaObj.content
	return mediaObj[ProductSharingImageSize.PostList]
}

function getPosterImage(userImageObj: any) {
	if (
		!userImageObj ||
		!Object.keys(userImageObj).length ||
		!userImageObj.content
	)
		return null
	if (!userImageObj.has_resize) return userImageObj.content
	return userImageObj[ProductSharingImageSize.Profile]
}

function initObserver() {
	const oberverRoot = document.querySelector(
		'.bs-product-sharing-post-list__content',
	)
	const options = {
		oberverRoot,
		threshold: [0, 0.25, 0.5, 0.75, 1],
	}
	const callback = (entries: IntersectionObserverEntry[]) => {
		entries.forEach((entry: any) => {
			if (
				entry.isIntersecting &&
				!entry.target.dataset.isSendEvent &&
				entry.target.dataset.postId
			) {
				// createGAEvent('ps_post_viewed', eventParams.value)
				entry.target.dataset.isSendEvent = 'true'
			}
		})
	}
	const observer = new IntersectionObserver(callback, options)
	const loadingObserver = postItem.value!
	observer.observe(loadingObserver)
}

function likePost() {
	likeIconCompoent.value?.like()
}

async function setCurrencySymbolOriginal() {
	currencySymbolOriginal.value = getCurrencySymbolOriginal({
		warehouse: props.post.warehouse,
		warehouseIdMap: props.warehouseIdMap,
		supportedCurrencies: props.supportedCurrencies,
	})
}

watch(
	() => props.post,
	val => {
		if (val !== null && Object.keys(props.post).length) {
			initObserver()
			if (props.isExplore && !props.isCurrentModeDefault) {
				setCurrencySymbolOriginal()
			}
		}
	},
)
</script>

<template>
	<div
		ref="postItem"
		:class="[
			'grid-item',
			'bs-product-sharing-post-list-item',
			{ 'new-gutter-of-search-and-index': isSearch || isExplore },
		]"
		:data-post-id="postId"
		data-is-send-event
	>
		<bs-normal-card :is-hovered-enabled="true" @content-click="viewPost">
			<template #content>
				<template v-if="isLoading">
					<div class="bs-product-sharing-post-list-item__image-skeleton">
						<div class="skeleton skeleton-line"></div>
					</div>
				</template>
				<template v-else>
					<div
						v-if="postItemInfo.isExpressCheckout"
						class="bs-product-sharing-post-list-item__symbols bs-product-sharing-post-list-item__symbols--l"
					>
						<span class="bs-icon bs-icon-easybuy"></span>
					</div>
					<div
						v-if="postSymbols"
						class="bs-product-sharing-post-list-item__symbols bs-product-sharing-post-list-item__symbols--r"
					>
						<div class="bs-icon-overlay"></div>
						<span class="bs-icon" :class="[`bs-icon-${postSymbols}`]"></span>
					</div>
					<div class="bs-product-sharing-post-list-item__image-container">
						<img
							class="bs-product-sharing-post-list-item__image"
							:style="{ height: `${calculateImageHeight}px` }"
							:src="postItemImageSrc"
							alt="post"
							loading="lazy"
						/>
					</div>
				</template>
				<div class="bs-product-sharing-post-list-item__info">
					<div class="bs-product-sharing-post-list-item__info-user">
						<div class="bs-product-sharing-post-list-item__info-user-avatar">
							<template v-if="!isLoading">
								<img
									v-if="postItemInfo && postItemInfo.user.image"
									class="bs-product-sharing-post-list-item__info-user-avatar-img"
									:src="getPosterImage(postItemInfo.user.image)"
									alt="Avatar"
								/>
								<img
									v-else
									class="bs-product-sharing-post-list-item__info-user-avatar-img"
									:src="getVueAssetImagePath('avatar_2_48px@2x.png')"
									alt="Avatar"
								/>
							</template>
							<template v-else>
								<div class="skeleton skeleton-line"></div>
							</template>
						</div>
						<div class="bs-product-sharing-post-list-item__info-user-detail">
							<div
								class="bs-product-sharing-post-list-item__info-user-detail-name"
							>
								<template v-if="!isLoading">
									<span
										class="bs-product-sharing-post-list-item__info-user-detail-name-text"
									>
										{{ postItemInfo.user.username }}
									</span>
									<BaseIcon
										class="bs-icon-member-official-1"
										v-if="isMemberOfficial1"
										name="badge"
									/>
									<span
										class="bs-icon-member-assigned-1"
										v-if="isMemberAssigned1"
									></span>
									<span
										class="bs-icon-member-assigned-2"
										v-if="isMemberAssigned2"
									></span>
								</template>
								<template v-else>
									<div class="skeleton skeleton-line"></div>
								</template>
							</div>
						</div>
					</div>

					<span class="bs-product-sharing-post-list-item__info-title">
						<template v-if="!isLoading">{{ postItemInfo.title }}</template>
						<template v-else>
							<div class="skeleton skeleton-line"></div>
						</template>
					</span>

					<div
						class="bs-product-sharing-post-list-item__info__price"
						v-if="isShowPrice"
					>
						<template v-if="!isLoading">
							{{ isLocalCurrency ? localPricing : priceOriginal }}
						</template>
						<template v-else>
							<div class="skeleton skeleton-line"></div>
						</template>
					</div>
					<div class="bs-product-sharing-post-list-item__info-action">
						<template v-if="!isLoading">
							<BsProductSharingLikeIcon
								class="bs-like-icon"
								ref="likeIconCompoent"
								:post-info="postItemInfo"
								:width="16"
								:height="16"
								:text="text"
								@update-post-info="updatePostInfo"
							/>
							<span
								v-if="totalLikeCountDisplay"
								class="bs-product-sharing-post-list-item__info-action-like"
							>
								{{ totalLikeCountDisplay }}
							</span>
						</template>
						<template v-else>
							<div class="skeleton skeleton-line"></div>
						</template>
					</div>
				</div>
			</template>
		</bs-normal-card>
	</div>
</template>
